// Practice and games: ids, levels and the shapes the practice API sends and receives.
// Shared by the browser and the server — no React, no engine and no server imports here.

import type { Num, Person } from '@/prakrit/lib/grammar/types'
import type { L } from '@/prakrit/lib/lessons/types'

export const PRACTICE_MODES = [
    'dhatu-chunauti',
    'kaal-pahchan',
    'roop-sadhana',
    'kridanta-pahchan',
    'vibhakti-chayan',
    'vachan-badlo',
    'vakya-nirmata',
    'anuvad',
    'sanskrit-prakrit',
    'sandhi-nirmata',
    'samas-pahchan',
    'shravan',
    'katha-kram'
] as const

export type PracticeMode = typeof PRACTICE_MODES[number]

/** आज का अभ्यास: a mixed session (due review items first). */
export const MIXED_MODE = 'aaj'

export type SessionMode = PracticeMode | typeof MIXED_MODE

export const LEVELS = ['arambh', 'abhyas', 'praveen'] as const

export type Level = typeof LEVELS[number]

/** Questions in one session. */
export const SESSION_LENGTH = 10

/** Seconds per question at प्रवीण, Adults only (Kids never get a timer). */
export const TIMER_SECONDS = 20

export const isPracticeMode = (value: unknown): value is PracticeMode => (
    typeof value === 'string' && (PRACTICE_MODES as readonly string[]).includes(value)
)

export const isSessionMode = (value: unknown): value is SessionMode => value === MIXED_MODE || isPracticeMode(value)

export const isLevel = (value: unknown): value is Level => typeof value === 'string' && (LEVELS as readonly string[]).includes(value)

/** Why a mode or level has nothing to ask right now. `no_topic`: the chosen topics leave nothing verified to ask. */
export type Unavailable = 'no_verified' | 'no_recordings' | 'no_stories' | 'no_focus' | 'no_topic' | 'needs_plan'

export interface ModeAvailability {
    levels: Record<Level, boolean>
    /** श्रवण: the levels that open when the device can read aloud with its own voice (no recording needed). */
    voiceLevels?: Record<Level, boolean>
    reason?: Unavailable
}

/** How a listening question sounds: a teacher's recording (URLs), or the device voice reading masked text. */
export interface QuestionAudio {
    normal?: string
    slow?: string
    /** Text for the device voice, masked (see listen-mask.ts); unmasked only when it plays. */
    say?: string
}

export interface ChoiceOption {
    id: string
    /** A Prakrit form or sentence. */
    pra?: string
    /** A Sanskrit word (shown with lang="sa"). */
    sa?: string
    hi?: string
    en?: string
}

/** One labelled fact of the question, e.g. कर्ता: अहं (मैं · I). */
export interface CueChip {
    label: L
    pra?: string
    hi?: string
    en?: string
}

export interface QuestionCue {
    /** Prakrit shown large (a form, a sentence, a compound, "जीव + अजीव"). */
    pra?: string
    /** Sanskrit shown large. */
    sa?: string
    /** Meaning (Hindi · English) of the cue. */
    hi?: string
    en?: string
    chips?: CueChip[]
    /** "plus": the chips read as an equation (कर्ता + धातु + काल = ?). */
    join?: 'plus' | 'none'
    note?: L
    /** The Prakrit form shown large is a विकल्प (variant) form (practice with variants included). */
    vikalp?: boolean
}

/** A विकल्प (variant) form in a right answer, with the standard form taught first. */
export interface VikalpForm {
    form: string
    core: string
}

export interface TableCellView {
    id: string
    person: Person
    number: Num
    subject: string
    /** Shown form (absent for a hidden cell). */
    form?: string
    /** Slot id of a hidden cell. */
    slot?: string
}

export interface TableSlot {
    id: string
    cell: string
    label: L
    options: ChoiceOption[]
}

export interface OrderLine {
    id: string
    pra: string
    hi: string
    en: string
    speaker?: string
}

export type QuestionBody =
    | { kind: 'choice', prompt: L, cue?: QuestionCue, skill: string, options: ChoiceOption[], audio?: QuestionAudio }
    | {
        kind: 'table'
        prompt: L
        cue?: QuestionCue
        skill: string
        table: { title: L, root: string, cells: TableCellView[], slots: TableSlot[] }
    }
    | { kind: 'build', prompt: L, cue?: QuestionCue, skill: string, target: L, tiles: { id: string, text: string }[], slots: number }
    | {
        kind: 'order'
        prompt: L
        cue?: QuestionCue
        skill: string
        story: { pra: string, hi: string, en: string }
        lines: OrderLine[]
        showMeaning: boolean
    }

export type QuestionKind = QuestionBody['kind']

export interface QuestionMeta {
    /** Signed by the server; holds the answer reference. */
    token: string
    mode: PracticeMode
    level: Level
    /** Short fingerprint of the item, sent back as `seen` so a session does not repeat itself. */
    item: string
    timed: boolean
    seconds: number
    /** Part of आज का अभ्यास. */
    mixed: boolean
    /** The question practises the requested root, word or review item. */
    focusUsed: boolean
    /** Part of a numbered practice set (see BankInfo). */
    set?: SetPlace
}

/** Where a question sits in its practice set. */
export interface SetPlace {
    /** The bank's key (for the learner's set results). */
    key: string
    /** The set's number, from 1. */
    set: number
    /** Sets in the bank. */
    sets: number
    /** Questions in this set (10, or fewer in the last set). */
    size: number
    /** Exercises in the whole bank. */
    total: number
}

/** A game's bank of exercises for one level and filter, cut into numbered sets. */
export interface BankInfo {
    key: string
    total: number
    sets: number
    setSize: number
    /** Sets from this number on need a plan or the trial; null when every set is open to this visitor. */
    lockedFrom: number | null
}

/** A learner's best result in one practice set. */
export interface SetRecord {
    best: number
    size: number
    plays: number
}

/** GET /api/practice/bank */
export type BankResponse = { bank: BankInfo } | { unavailable: Unavailable }

export type PracticeQuestion = QuestionMeta & QuestionBody

/** GET /api/practice/question */
export type QuestionResponse = { question: PracticeQuestion } | { unavailable: Unavailable }

/** POST /api/practice/answer { token, answer } */
export interface PracticeAnswer {
    id?: string
    slots?: Record<string, string>
    tiles?: string[]
    order?: string[]
    /** The 20-second timer ran out. */
    timeout?: boolean
}

export interface PracticeAttempt {
    ref: string
    skill: string
    correct: boolean
    code?: string
    label?: string
}

export interface SlotResult {
    id: string
    correct: boolean
    expected: string
    expectedId: string
    /** The expected form is a विकल्प form: the standard form of the cell. */
    vikalp?: string
}

export interface AnswerResponse {
    correct: boolean
    /** The rule, written by the authors (Hindi + English). */
    feedback: L
    /** What exactly was wrong (wrong answers only). */
    reason?: L
    note?: L
    /** The right answer as text (Prakrit, or a label). */
    expected?: string
    /** Meaning of the right answer, when it has one. */
    expectedMeaning?: L
    expectedId?: string
    slotResults?: SlotResult[]
    expectedOrder?: string[]
    /** The right answer uses विकल्प (variant) forms; sent only after grading, so it is never a hint. */
    vikalp?: VikalpForm[]
    timedOut?: boolean
    code?: string
    ref: string
    skill: string
    label?: string
    xpAwarded: number
    receipt?: string
    attempt: PracticeAttempt
    attempts: PracticeAttempt[]
    /** This question was answered before: the first result, with nothing new recorded. */
    repeat?: boolean
}

/** One answered question of a session (for the summary). */
export interface PlayedQuestion {
    index: number
    question: PracticeQuestion
    result: AnswerResponse
}
